/**
 * Open Interest (OI) Analysis Overlay Profile Calculations.
 *
 * Computes pixel geometry for horizontal Call/Put open interest bars anchored to price levels on the chart overlay canvas.
 */

export interface OIProfileStrikeData {
  strike: number;
  ce_oi?: number;
  pe_oi?: number;
}

export interface OIProfileRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface OIProfileBar {
  strike: number;
  ce_oi: number;
  pe_oi: number;
  ceRect: OIProfileRect;
  peRect: OIProfileRect;
}

export interface OIProfileSettings {
  max_bar_width?: number;
  bar_thickness?: number;
}

export type PriceToCoordinateFn = (price: number) => number | null;

/**
 * Computes horizontal OI profile bar positions and dimensions for visible strikes.
 */
export function computeOIProfileBars(
  data: OIProfileStrikeData[] | null | undefined,
  priceToCoordinate: PriceToCoordinateFn,
  canvasWidth: number,
  canvasHeight: number,
  priceScaleWidth: number = 0,
  settings: OIProfileSettings = {},
): OIProfileBar[] {
  if (
    !Array.isArray(data) ||
    !data.length ||
    typeof priceToCoordinate !== "function"
  ) {
    return [];
  }

  const maxBarWidth =
    settings.max_bar_width != null ? settings.max_bar_width : 120;
  const rowHeight =
    settings.bar_thickness != null ? settings.bar_thickness : 16;
  const halfRow = rowHeight / 2;
  const rightEdge = canvasWidth - priceScaleWidth - 4; // 4px gap before the scale

  const visible: Array<{ s: OIProfileStrikeData; y: number }> = [];
  let maxOi = 0;

  for (const s of data) {
    if (!s) continue;
    const y = priceToCoordinate(s.strike);
    if (y == null) continue;
    if (y < 0 || y > canvasHeight) continue;
    visible.push({ s, y });
    const ce = s.ce_oi || 0;
    const pe = s.pe_oi || 0;
    if (ce > maxOi) maxOi = ce;
    if (pe > maxOi) maxOi = pe;
  }

  if (!visible.length) return [];
  if (maxOi <= 0) maxOi = 1;

  return visible.map(({ s, y }) => {
    const ce = s.ce_oi || 0;
    const pe = s.pe_oi || 0;
    const ceLen = (ce / maxOi) * maxBarWidth;
    const peLen = (pe / maxOi) * maxBarWidth;
    return {
      strike: s.strike,
      ce_oi: ce,
      pe_oi: pe,
      ceRect: { x: rightEdge - ceLen, y: y - halfRow, w: ceLen, h: halfRow },
      peRect: { x: rightEdge - peLen, y, w: peLen, h: halfRow },
    };
  });
}

/**
 * Tests whether a point (px, py) lies within any bar's Call or Put rectangle.
 */
export function findHoveredOIProfileBar(
  bars: OIProfileBar[],
  px: number,
  py: number,
): OIProfileBar | null {
  if (!Array.isArray(bars)) return null;
  for (const bar of bars) {
    const inCe =
      px >= bar.ceRect.x &&
      px <= bar.ceRect.x + bar.ceRect.w &&
      py >= bar.ceRect.y &&
      py <= bar.ceRect.y + bar.ceRect.h;
    const inPe =
      px >= bar.peRect.x &&
      px <= bar.peRect.x + bar.peRect.w &&
      py >= bar.peRect.y &&
      py <= bar.peRect.y + bar.peRect.h;
    if (inCe || inPe) return bar;
  }
  return null;
}
